import styled, { keyframes } from 'styled-components';
import { ThemeType } from '../../types/command';
import { themeTokens } from '../../styles/theme';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const blink = keyframes`
  50% { opacity: 0; }
`;

// ========== PAGE CONTAINER ==========
export const ServicesPageContainer = styled.div<{ $themeName: ThemeType }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  margin: 0;
  padding: 0;
  background-color: ${({ $themeName }) => themeTokens[$themeName].bg};
  color: ${({ $themeName }) => themeTokens[$themeName].text};
  font-family: ${({ $themeName }) => themeTokens[$themeName].font};
  overflow-x: hidden;
  -webkit-tap-highlight-color: transparent;
`;

// ========== HEADER ==========
export const Header = styled.header<{ $themeName: ThemeType; $scrolled: boolean }>`
  width: 100%;
  height: 64px;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  background-color: ${({ $themeName }) => `${themeTokens[$themeName].bg}dd`};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};
  transition: box-shadow 0.3s ease;
  box-shadow: ${({ $scrolled }) => $scrolled ? '0 2px 20px rgba(0, 0, 0, 0.3)' : 'none'};
`;

export const HeaderContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const HeaderLogo = styled.span<{ $themeName: ThemeType }>`
  font-size: 18px;
  font-weight: 700;
  color: ${({ $themeName }) => themeTokens[$themeName].text};
  white-space: nowrap;
`;

export const HeaderNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavLink = styled.a<{ $themeName: ThemeType }>`
  padding: 8px 14px;
  font-size: 14px;
  color: ${({ $themeName }) => themeTokens[$themeName].textSecondary};
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;
  user-select: none;

  &:hover {
    color: ${({ $themeName }) => themeTokens[$themeName].accent};
  }
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const HeaderButton = styled.button<{ $themeName: ThemeType; $variant?: 'primary' | 'ghost' }>`
  padding: 8px 16px;
  font-family: inherit;
  font-size: 13px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  border: 1px solid ${({ $themeName }) => themeTokens[$themeName].accent};
  background: ${({ $variant, $themeName }) =>
    $variant === 'primary' ? themeTokens[$themeName].accent : 'transparent'};
  color: ${({ $variant, $themeName }) =>
    $variant === 'primary'
      ? ($themeName === 'powershell' ? themeTokens[$themeName].bg : '#fff')
      : themeTokens[$themeName].accent};

  &:hover {
    background: ${({ $themeName }) => themeTokens[$themeName].accent};
    color: ${({ $themeName }) =>
      $themeName === 'powershell' ? themeTokens[$themeName].bg : '#fff'};
  }

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`;

// Mobile menu toggle
export const MenuToggle = styled.button<{ $themeName: ThemeType; $isOpen: boolean }>`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  width: 28px;
  height: 20px;
  padding: 0;
  position: relative;

  span {
    display: block;
    width: 100%;
    height: 2px;
    background-color: ${({ $themeName }) => themeTokens[$themeName].accent};
    position: absolute;
    left: 0;
    transition: transform 0.3s ease, opacity 0.3s ease;

    &:nth-child(1) {
      top: 0;
      transform: ${({ $isOpen }) => $isOpen ? 'rotate(45deg) translate(6px, 6px)' : 'none'};
    }
    &:nth-child(2) {
      top: 9px;
      opacity: ${({ $isOpen }) => $isOpen ? 0 : 1};
    }
    &:nth-child(3) {
      top: 18px;
      transform: ${({ $isOpen }) => $isOpen ? 'rotate(-45deg) translate(6px, -6px)' : 'none'};
    }
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

// Mobile overlay menu
export const MobileMenuOverlay = styled.div<{ $themeName: ThemeType; $isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background-color: ${({ $themeName }) => `${themeTokens[$themeName].bg}f5`};
    backdrop-filter: blur(20px);
    z-index: 999;
    opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
    pointer-events: ${({ $isOpen }) => $isOpen ? 'auto' : 'none'};
    transition: opacity 0.3s ease;
  }
`;

export const MobileNavLink = styled.a<{ $themeName: ThemeType }>`
  font-size: 24px;
  color: ${({ $themeName }) => themeTokens[$themeName].text};
  text-decoration: none;
  padding: 12px 24px;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ $themeName }) => themeTokens[$themeName].accent};
  }
`;

// ========== HERO ==========
export const HeroSection = styled.section<{ $themeName: ThemeType }>`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 120px 24px 80px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
      ${({ $themeName }) => themeTokens[$themeName].border} 1px,
      transparent 1px
    );
    background-size: 30px 30px;
    opacity: 0.3;
    pointer-events: none;
  }
`;

export const HeroPrompt = styled.div<{ $themeName: ThemeType }>`
  font-size: 16px;
  color: ${({ $themeName }) => themeTokens[$themeName].muted};
  margin-bottom: 8px;
  font-family: ${({ $themeName }) => themeTokens[$themeName].font};
`;

export const HeroTitle = styled.h1<{ $themeName: ThemeType }>`
  font-size: 48px;
  font-weight: 700;
  color: ${({ $themeName }) => themeTokens[$themeName].accent};
  margin: 0 0 16px 0;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

export const HeroDescription = styled.p<{ $themeName: ThemeType }>`
  font-size: 20px;
  color: ${({ $themeName }) => themeTokens[$themeName].textSecondary};
  margin: 0 0 24px 0;
  text-align: center;
  max-width: 700px;
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

export const BlinkingCursor = styled.span<{ $themeName: ThemeType }>`
  display: inline-block;
  width: 10px;
  height: 20px;
  background-color: ${({ $themeName }) => themeTokens[$themeName].accent};
  margin-left: 4px;
  vertical-align: middle;
  animation: ${blink} 1s step-end infinite;
`;

// ========== CONTENT ==========
export const ContentContainer = styled.main`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 480px) {
    padding: 0 16px;
  }
`;

// ========== SECTION TITLE ==========
export const SectionTitle = styled.h2<{ $themeName: ThemeType }>`
  font-size: 28px;
  margin: 0 0 32px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid ${({ $themeName }) => themeTokens[$themeName].accent};
  display: inline-block;

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

export const SectionPrompt = styled.span<{ $themeName: ThemeType }>`
  display: block;
  font-size: 13px;
  color: ${({ $themeName }) => themeTokens[$themeName].muted};
  margin-bottom: 8px;
  font-family: ${({ $themeName }) => themeTokens[$themeName].font};
`;

// ========== SERVICES GRID ==========
export const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 20px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const ServiceCard = styled.div<{ $themeName: ThemeType }>`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  border-radius: 8px;
  background-color: ${({ $themeName }) => themeTokens[$themeName].bgSecondary};
  border: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};
  border-left: 3px solid ${({ $themeName }) => themeTokens[$themeName].accent};
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ $themeName }) => themeTokens[$themeName].accent};
    box-shadow: 0 4px 20px ${({ $themeName }) => themeTokens[$themeName].shadow};
  }
`;

export const ServiceIconWrapper = styled.div<{ $themeName: ThemeType }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 50%;
  background-color: ${({ $themeName }) => themeTokens[$themeName].bgTertiary};
  color: ${({ $themeName }) => themeTokens[$themeName].accent};
  transition: transform 0.3s ease;

  svg {
    width: 22px;
    height: 22px;
  }

  ${ServiceCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }
`;

export const ServiceCardTitle = styled.h3<{ $themeName: ThemeType }>`
  font-size: 17px;
  color: ${({ $themeName }) => themeTokens[$themeName].text};
  margin: 0 0 8px 0;
`;

export const ServiceCardDescription = styled.p<{ $themeName: ThemeType }>`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ $themeName }) => themeTokens[$themeName].textSecondary};
  margin: 0;
`;

// ========== CONTACT ==========
export const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

export const ContactCard = styled.div<{ $themeName: ThemeType; $accentColor: string }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  border-radius: 8px;
  background-color: ${({ $themeName }) => themeTokens[$themeName].bgSecondary};
  border: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};
  border-top: 3px solid ${({ $accentColor }) => `${$accentColor}60`};
  cursor: pointer;
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    border-top-color: ${({ $accentColor }) => $accentColor};
    box-shadow: 0 4px 20px ${({ $themeName }) => themeTokens[$themeName].shadow};
  }
`;

export const ContactCardIcon = styled.div<{ $accentColor: string }>`
  width: 56px;
  height: 56px;
  min-width: 56px;
  border-radius: 50%;
  background-color: ${({ $accentColor }) => `${$accentColor}15`};
  border: 2px solid ${({ $accentColor }) => `${$accentColor}30`};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease, background-color 0.3s ease;

  ${ContactCard}:hover & {
    transform: scale(1.1) rotate(5deg);
    background-color: ${({ $accentColor }) => `${$accentColor}25`};
  }
`;

export const ContactCardContent = styled.div`
  flex: 1;
`;

export const ContactCardTitle = styled.h4<{ $themeName: ThemeType }>`
  font-size: 16px;
  color: ${({ $themeName }) => themeTokens[$themeName].text};
  margin: 0 0 4px 0;
`;

export const ContactCardDescription = styled.p<{ $themeName: ThemeType }>`
  font-size: 13px;
  color: ${({ $themeName }) => themeTokens[$themeName].textSecondary};
  margin: 0 0 12px 0;
  line-height: 1.5;
`;

export const ContactCardButton = styled.button<{ $accentColor: string }>`
  background-color: transparent;
  color: ${({ $accentColor }) => $accentColor};
  border: 1px solid ${({ $accentColor }) => $accentColor};
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  ${ContactCard}:hover & {
    background-color: ${({ $accentColor }) => $accentColor};
    color: #ffffff;
  }
`;

// ========== FOOTER ==========
export const FooterContainer = styled.footer<{ $themeName: ThemeType }>`
  width: 100%;
  margin-top: 64px;
  border-top: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};
  background-color: ${({ $themeName }) => themeTokens[$themeName].bgSecondary};
`;

export const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 48px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
    padding: 32px 24px;
  }
`;

export const FooterBrand = styled.div<{ $themeName: ThemeType }>`
  h3 {
    font-size: 20px;
    margin: 0 0 8px 0;
    color: ${({ $themeName }) => themeTokens[$themeName].text};
  }

  p {
    font-size: 14px;
    color: ${({ $themeName }) => themeTokens[$themeName].textSecondary};
    margin: 0 0 16px 0;
    line-height: 1.6;
  }
`;

export const FooterPrompt = styled.span<{ $themeName: ThemeType }>`
  font-size: 13px;
  color: ${({ $themeName }) => themeTokens[$themeName].muted};
  font-family: ${({ $themeName }) => themeTokens[$themeName].font};
`;

export const FooterColumn = styled.div<{ $themeName: ThemeType }>`
  h4 {
    font-size: 15px;
    margin: 0 0 16px 0;
    color: ${({ $themeName }) => themeTokens[$themeName].text};
  }
`;

export const FooterLink = styled.a<{ $themeName: ThemeType }>`
  display: block;
  font-size: 14px;
  color: ${({ $themeName }) => themeTokens[$themeName].textSecondary};
  text-decoration: none;
  padding: 4px 0;
  transition: color 0.2s ease;
  cursor: pointer;

  &:hover {
    color: ${({ $themeName }) => themeTokens[$themeName].accent};
  }
`;

export const FooterBottom = styled.div<{ $themeName: ThemeType }>`
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 24px;
  border-top: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};
  text-align: center;
  font-size: 13px;
  color: ${({ $themeName }) => themeTokens[$themeName].muted};
`;
